import type { ReactElement } from "react";
import { NotYetDeveloped } from "@/components/NotYetDeveloped/NotYetDeveloped";
import type { HeadlineVariantProps, PostSubmitContext } from "@/types/config";
import { resolveComponentRef } from "./component-configs";
import { sectionRegistry } from "./section-components";
import { getResolvedSectionComponent } from "./section-lazy-loaders";
// Zod-free by contract — see section-roles.ts. This file is client-bundled.
import { roleForSectionType } from "./section-roles";
import type { ComponentRef, SectionComponent, SectionConfig } from "./types";
import { isComponentRef } from "./types";

export type SectionType = keyof typeof sectionRegistry;

export function getSectionComponent(type: string): SectionComponent | undefined {
  // Code-split section components (see registry/section-lazy-loaders.ts) are not
  // in the static sectionRegistry; they resolve here once preloaded before
  // render/hydrate in +onRenderHtml / +onRenderClient.
  return sectionRegistry[type] ?? getResolvedSectionComponent(type);
}

function resolveSectionRole(section: SectionConfig): string {
  if (section.role) return section.role;
  return roleForSectionType(section.type);
}

export interface RenderSectionContext {
  headlineVariant?: HeadlineVariantProps | null;
  postSubmit?: PostSubmitContext | null;
}

export function renderSection(
  section: SectionConfig | ComponentRef,
  index: number,
  ctx?: RenderSectionContext,
): ReactElement {
  let resolvedSection: SectionConfig;

  if (isComponentRef(section)) {
    const resolved = resolveComponentRef(section);
    if (!resolved) {
      return (
        <div key={`section-${index}`}>
          <NotYetDeveloped type={section.$ref} props={{}} />
        </div>
      );
    }
    resolvedSection = resolved;
  } else {
    resolvedSection = section;
  }

  const Component = getSectionComponent(resolvedSection.type);
  const key = resolvedSection.id || `section-${index}`;

  if (!Component) {
    return (
      <NotYetDeveloped
        key={key}
        type={resolvedSection.type}
        props={resolvedSection.props}
        id={resolvedSection.id}
      />
    );
  }

  const sectionName = resolvedSection.type;
  const sectionRole = resolveSectionRole(resolvedSection);
  const sectionId = resolvedSection.id;

  // Dynamic-headline bandit (ADR-0069): a standard HERO section merges the two
  // innocuous string keys (headline/subhead) into its props so a kit hero
  // (e.g. kit-guide-hero) renders the bandit-selected H1/subhead. Keyed on
  // the resolved role so ANY hero section works regardless of `type`. Fires
  // only when a variant was resolved for this request (only on
  // headline-active pages); every other section/page is untouched.
  const headlineOverride =
    sectionRole === "hero" && ctx?.headlineVariant
      ? {
          headline: ctx.headlineVariant.headline,
          subhead: ctx.headlineVariant.subhead ?? undefined,
        }
      : undefined;

  // Post-submit context (email/source/kind) forwarded only to the
  // lead-magnet-next-steps section so it SSR-renders the correct copy variant.
  const postSubmit =
    resolvedSection.type === "lead-magnet-next-steps" && ctx?.postSubmit
      ? ctx.postSubmit
      : undefined;

  const component = (
    <Component
      {...resolvedSection.props}
      {...headlineOverride}
      background={resolvedSection.background}
      padding={resolvedSection.padding}
      pattern={resolvedSection.pattern}
      className={resolvedSection.className}
      id={resolvedSection.id}
      postSubmit={postSubmit}
    />
  );

  // ⚠️ Never set `animate` on a section that hosts a fixed overlay (a modal,
  // drawer, lightbox — anything reading the --z-index-* scale). `opacity: 0`
  // (every [data-animate] value starts there, styles/global.css) creates a
  // stacking context that traps the overlay's z-index inside this section
  // instead of the page-level order; fadeInUp/scaleIn's `transform` ALSO
  // makes this element a containing block for `position: fixed`, so a
  // full-viewport `inset: 0` overlay resolves against this small animated
  // box instead of the viewport (docs/specs/website/design-token-linting.md
  // "The data-animate landmine"). fadeIn traps the z-index without breaking
  // inset: 0; the other two do both.
  if (resolvedSection.animate) {
    return (
      <div
        key={key}
        data-animate={resolvedSection.animate}
        data-section={sectionName}
        data-section-role={sectionRole}
        data-section-id={sectionId}
        data-section-index={index}
        suppressHydrationWarning
      >
        {component}
      </div>
    );
  }

  return (
    <div
      key={key}
      data-section={sectionName}
      data-section-role={sectionRole}
      data-section-id={sectionId}
      data-section-index={index}
    >
      {component}
    </div>
  );
}
